import { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile widget. Loads the CF challenge script once per page
 * (idempotent via a global guard), mounts an invisible-ish challenge in the
 * given container, and pushes the resulting token to `onToken`.
 *
 * Re-renders are a no-op — the widget is mounted once per lifecycle. On
 * expiry / refresh / explicit reset, the callback fires with `null` so the
 * parent can disable the submit button until a fresh token arrives.
 *
 * The widget renders visibly on every mount (appearance='always') so the
 * operator can visually confirm the challenge is present on every gated form.
 * We tried `appearance='interaction-only'` early on, but the widget staying
 * invisible for most legitimate visitors made it look broken during launch
 * QA (Cloudflare silently validates trusted browsers with no visible pixel
 * change). The trade — a small "success" badge under each form for every
 * visitor — is worth the operational clarity.
 *
 * Renders nothing if `siteKey` is falsy — used by `useAbuseConfig` to gate
 * the widget on whether the operator has configured Turnstile.
 */

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileWindow extends Window {
  turnstile?: {
    render: (
      container: HTMLElement,
      opts: {
        sitekey: string;
        callback: (token: string) => void;
        'error-callback'?: () => void;
        'expired-callback'?: () => void;
        appearance?: 'always' | 'execute' | 'interaction-only';
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'flexible' | 'compact';
      },
    ) => string;
    reset: (widgetId: string) => void;
    remove: (widgetId: string) => void;
  };
}

declare const window: TurnstileWindow;

let scriptLoadingPromise: Promise<void> | null = null;

function loadCloudflareScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptLoadingPromise) return scriptLoadingPromise;
  scriptLoadingPromise = new Promise<void>((resolve, reject) => {
    if (document.getElementById(SCRIPT_ID)) {
      // Already inserted by a prior mount — poll briefly for the global
      const start = Date.now();
      const tick = () => {
        if (window.turnstile) return resolve();
        if (Date.now() - start > 5000) return reject(new Error('Turnstile script timeout'));
        setTimeout(tick, 50);
      };
      tick();
      return;
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    // Without crossOrigin, an error thrown inside the CF script is scrubbed
    // to bare "Script error." (no message, no stack) in window.onerror — which
    // is what PostHog Error Tracking then reports. Setting it to anonymous
    // lets the browser expose the real error, so we can tell noise from bugs.
    s.crossOrigin = 'anonymous';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(s);
  });
  return scriptLoadingPromise;
}

interface Props {
  siteKey: string | null;
  onToken: (token: string | null) => void;
  /** Optional theme override; defaults to 'auto' (follows OS / browser). */
  theme?: 'light' | 'dark' | 'auto';
}

export function TurnstileWidget({ siteKey, onToken, theme = 'auto' }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Stash the latest onToken so the render() callback always points at the
  // current closure without forcing a remount.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;
    let cancelled = false;
    const container = containerRef.current;
    loadCloudflareScript()
      .then(() => {
        if (cancelled || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(container, {
          sitekey: siteKey,
          theme,
          // `normal` (300×65 fixed) instead of `flexible` — flexible needs a
          // stable parent width and can render at 0px on narrow/hidden layouts;
          // normal is the most reliable across every browser + form container.
          size: 'normal',
          // `always` instead of `interaction-only` — see the top-of-file
          // docstring for why: interaction-only hides the widget when the
          // visitor is silently validated, which looks broken during QA.
          appearance: 'always',
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
        });
      })
      .catch(() => {
        // Script-load failure: never block the form — fall through with no
        // token. If the operator has Turnstile enforced server-side, the
        // backend will reject with a 403 the form's error handler shows.
        onTokenRef.current(null);
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore — widget may already be torn down by React strict-mode double-invoke
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme]);

  if (!siteKey) return null;
  return <div ref={containerRef} className="min-h-[65px]" data-testid="turnstile-widget" />;
}

/**
 * `TurnstileWidget` that holds its slot open but does not load Cloudflare until
 * the visitor actually engages with the form — see `useTurnstileGate` for why
 * (the challenge is ~1.3 MB of third-party payload, and most visitors on a free
 * tool page never submit).
 *
 * The outer div reserves the widget's 65px whether or not it has mounted, so
 * arming it does not push the page around. Layout shifts within 500ms of a real
 * interaction are excluded from CLS anyway, but reserving the space keeps the
 * form from twitching under the visitor's cursor, which they would notice.
 *
 * Pass the whole gate rather than its pieces so a page cannot wire up the
 * widget and forget `armProps` — without arming, the widget would never mount
 * and the submit button would stay disabled forever.
 */
export function DeferredTurnstileWidget({
  gate,
  theme,
}: {
  gate: { siteKey: string | null; armed: boolean; setToken: (t: string | null) => void };
  theme?: 'light' | 'dark' | 'auto';
}) {
  if (!gate.siteKey) return null;
  return (
    <div className="min-h-[65px]" data-testid="turnstile-slot">
      {gate.armed && (
        <TurnstileWidget siteKey={gate.siteKey} onToken={gate.setToken} theme={theme} />
      )}
    </div>
  );
}
